import { type Node, type NodeProps, Position } from '@xyflow/react';
import React from 'react';

import { BaseNode, type NodeData } from './BaseNode';
import { NodeHandle } from './NodeHandle';

// Helper function to convert JSON schema to type description
const getSchemaTypeDescription = (
  schema: unknown,
  maxLength: number = 50,
): string => {
  if (!schema || typeof schema !== 'object') return 'any';

  const schemaObj = schema as Record<string, unknown>;

  // Handle basic types
  if (schemaObj.type) {
    const type = schemaObj.type as string;

    switch (type) {
      case 'string':
        return 'string';
      case 'number':
      case 'integer':
        return 'number';
      case 'boolean':
        return 'boolean';
      case 'null':
        return 'null';
      case 'array':
        if (schemaObj.items) {
          const itemType = getSchemaTypeDescription(schemaObj.items, 20);
          return `list(${itemType})`;
        }
        return 'list(any)';
      case 'object':
        if (schemaObj.properties) {
          const props = schemaObj.properties as Record<string, unknown>;
          const keys = Object.keys(props);

          if (keys.length === 0) return 'object({})';

          const propDescriptions: string[] = [];
          let currentLength = 8; // Start with 'object()'

          for (const key of keys) {
            const propType = getSchemaTypeDescription(props[key], 15);
            const propDesc = `"${key}": ${propType}`;

            // Check if adding this property would exceed max length
            if (currentLength + propDesc.length + 2 > maxLength) {
              // +2 for ', '
              propDescriptions.push('...');
              break;
            }

            propDescriptions.push(propDesc);
            currentLength += propDesc.length + 2;
          }

          const content = propDescriptions.join(', ');
          return `object({${content}})`;
        }
        return 'object({})';
      default:
        return type;
    }
  }

  // Handle union types (oneOf, anyOf, allOf)
  if (schemaObj.oneOf && Array.isArray(schemaObj.oneOf)) {
    const types = (schemaObj.oneOf as unknown[]).map((s) =>
      getSchemaTypeDescription(s, 15),
    );
    return types.join(' | ');
  }

  // Handle properties-only objects (implicit object type)
  if (schemaObj.properties) {
    return getSchemaTypeDescription(
      { ...schemaObj, type: 'object' },
      maxLength,
    );
  }

  return 'any';
};

export type InputNodeType = Node<NodeData, 'custom_input'>;

export const InputNode: React.FC<NodeProps<InputNodeType>> = ({
  id,
  data,
  selected,
}) => {
  const outputPort = (data.config?.output_port as string) || 'data';
  const jsonSchema = data.config?.json_schema;

  let subtitle: string | undefined;
  if (jsonSchema) {
    const typeDescription = getSchemaTypeDescription(jsonSchema);
    subtitle = typeDescription;
  }

  return (
    <BaseNode
      nodeId={id}
      data={data}
      category="io"
      subtitle={subtitle}
      selected={selected}
    >
      {/* Output handle for input/constant blocks */}
      <NodeHandle
        type="source"
        position={Position.Right}
        id={outputPort}
        blockId={id}
      />
    </BaseNode>
  );
};
